import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Loader2, QrCode, ShieldCheck, Smartphone } from "lucide-react";

// ----------------------------------------------------------------
// Passos de configuração (exibidos no aviso pós-login).
// ----------------------------------------------------------------

const PASSOS = [
  { icon: Smartphone, key: "mfa.step1" },
  { icon: QrCode, key: "mfa.step2" },
  { icon: KeyRound, key: "mfa.step3" },
];

// ----------------------------------------------------------------
// Popup para digitar o código TOTP durante o login.
// ----------------------------------------------------------------

interface MfaCodeDialogProps {
  open: boolean;
  invalid: boolean;
  loading: boolean;
  onSubmit: (code: string) => void;
  onCancel: () => void;
}

export function MfaCodeDialog({
  open,
  invalid,
  loading,
  onSubmit,
  onCancel,
}: MfaCodeDialogProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");

  useEffect(() => {
    if (open) setCode("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {t("mfa.twoStepTitle")}
          </DialogTitle>
          <DialogDescription>{t("mfa.codeDialogDesc")}</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length >= 6) onSubmit(code);
          }}
          className="space-y-4"
        >
          <Input
            autoFocus
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            className="text-center text-lg tracking-[0.5em]"
          />

          {invalid && (
            <p className="text-center text-sm text-destructive">
              {t("mfa.invalidCode")}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || code.length < 6}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Validando...
                </>
              ) : (
                "Entrar"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------
// Popup que recomenda ativar o MFA após o login.
// A configuração é feita no backoffice (inovacode.app.br).
// ----------------------------------------------------------------

interface MfaEnrollDialogProps {
  open: boolean;
  onConfigure: () => void;
  onDismiss: () => void;
}

export function MfaEnrollDialog({
  open,
  onConfigure,
  onDismiss,
}: MfaEnrollDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onDismiss()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {t("mfa.enrollTitle")}
          </DialogTitle>
          <DialogDescription>{t("mfa.enrollDesc")}</DialogDescription>
        </DialogHeader>

        <ol className="space-y-3 py-2">
          {PASSOS.map((passo, index) => {
            const Icon = passo.icon;
            return (
              <li key={index} className="flex items-start gap-3">
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {index + 1}
                </span>
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon className="h-4 w-4 shrink-0" />
                  {t(passo.key)}
                </span>
              </li>
            );
          })}
        </ol>

        <DialogFooter>
          <Button variant="outline" onClick={onDismiss}>
            {t("mfa.dontAddNow")}
          </Button>
          <Button onClick={onConfigure}>{t("mfa.configureNow")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
